// Draft review card. Collapses to a single line; expands inline to show
// the original snippet, Jarvis's draft, and four actions:
//   ✅ Send This  |  ✏ Edit First  |  🔄 Redraft  |  ✕ Skip
//
// When the owner sends an edited version, the API returns
// significantEdit: true if >20% changed — this card surfaces the
// "Jarvis noticed" confirmation card right after.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { apiClient, unwrap } from '@/lib/apiClient'
import type { EmailDraftListItem, SendDraftResponse } from '@/types'

interface DraftDetail {
  record: EmailDraftListItem
  currentBody: string
}

interface Props {
  item: EmailDraftListItem
  onChanged: () => void
}

function typeBadge(type: string): { label: string; color: string } {
  switch (type) {
    case 'client':
      return {
        label: 'Client',
        color: 'text-jarvis-blue border-jarvis-blue/40 bg-jarvis-blue/10',
      }
    case 'lead':
      return {
        label: 'Lead',
        color: 'text-jarvis-red border-jarvis-red/40 bg-jarvis-red/10',
      }
    case 'vendor':
      return {
        label: 'Vendor',
        color: 'text-jarvis-muted border-jarvis-border bg-jarvis-surface-2',
      }
    case 'financial':
      return {
        label: 'Financial',
        color: 'text-jarvis-amber border-jarvis-amber/40 bg-jarvis-amber/10',
      }
    default:
      return {
        label: 'Other',
        color: 'text-jarvis-muted border-jarvis-border bg-jarvis-surface-2',
      }
  }
}

export function EmailDraftCard({ item, onChanged }: Props) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftDraft, setDraftDraft] = useState('') // local edit buffer
  const [showRedraft, setShowRedraft] = useState(false)
  const [redraftNote, setRedraftNote] = useState('')
  const [showJarvisNoticed, setShowJarvisNoticed] = useState(false)

  const detail = useQuery<DraftDetail>({
    queryKey: ['email-draft', item.id],
    queryFn: () => unwrap<DraftDetail>(apiClient.get(`/email/drafts/${item.id}`)),
    enabled: expanded,
  })

  useEffect(() => {
    if (detail.data) setDraftDraft(detail.data.currentBody)
  }, [detail.data?.currentBody])

  const send = useMutation({
    mutationFn: (body?: string) =>
      unwrap<SendDraftResponse>(
        apiClient.post(`/email/drafts/${item.id}/send`, body ? { body } : {}),
      ),
    onSuccess: (data) => {
      if (data.significantEdit) {
        setShowJarvisNoticed(true)
      } else {
        onChanged()
      }
      qc.invalidateQueries({ queryKey: ['email-drafts'] })
      qc.invalidateQueries({ queryKey: ['email-time-savings'] })
    },
  })

  const skip = useMutation({
    mutationFn: () => unwrap(apiClient.post(`/email/drafts/${item.id}/skip`)),
    onSuccess: () => {
      onChanged()
      qc.invalidateQueries({ queryKey: ['email-drafts'] })
    },
  })

  const redraft = useMutation({
    mutationFn: (note: string) =>
      unwrap<{ success: true; body: string }>(
        apiClient.post(`/email/drafts/${item.id}/redraft`, { note }),
      ),
    onSuccess: ({ body }) => {
      setDraftDraft(body)
      setShowRedraft(false)
      setRedraftNote('')
      qc.invalidateQueries({ queryKey: ['email-draft', item.id] })
    },
  })

  const badge = typeBadge(item.emailType)

  return (
    <JarvisCard>
      <button
        onClick={() => setExpanded((s) => !s)}
        className="w-full flex items-center gap-3 text-left"
      >
        {expanded ? (
          <ChevronDown size={18} className="text-jarvis-muted shrink-0" />
        ) : (
          <ChevronRight size={18} className="text-jarvis-muted shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-jarvis-text truncate">
              {item.from ?? '(unknown)'}
            </span>
            <span
              className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${badge.color}`}
            >
              {badge.label}
            </span>
          </div>
          <div className="text-sm text-jarvis-muted truncate">
            {item.subject ?? '(no subject)'}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {detail.isLoading ? (
            <p className="text-sm text-jarvis-muted">Loading draft…</p>
          ) : (
            <>
              {item.snippet && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5">
                    Original
                  </div>
                  <div className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg p-3 text-sm text-jarvis-muted whitespace-pre-wrap">
                    {item.snippet}
                    {item.snippet.length >= 200 && '…'}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5">
                  Jarvis draft
                </div>
                {editing ? (
                  <textarea
                    value={draftDraft}
                    onChange={(e) => setDraftDraft(e.target.value)}
                    rows={Math.max(6, draftDraft.split('\n').length + 1)}
                    className="bg-jarvis-surface-2 border border-jarvis-blue/40 rounded-lg p-3 text-sm w-full focus:outline-none focus:border-jarvis-blue/80 whitespace-pre-wrap"
                  />
                ) : (
                  <div className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg p-3 text-sm whitespace-pre-wrap">
                    {draftDraft || '(empty draft)'}
                  </div>
                )}
              </div>

              {item.sourcesUsed.length > 0 && (
                <div className="text-xs text-jarvis-muted">
                  Jarvis used: {item.sourcesUsed.join(' · ')}
                </div>
              )}

              {showRedraft && (
                <div className="border border-jarvis-blue/30 rounded-lg p-3 bg-jarvis-blue/5 space-y-2">
                  <label className="text-xs uppercase tracking-widest text-jarvis-muted block">
                    What should Jarvis change?
                  </label>
                  <input
                    value={redraftNote}
                    onChange={(e) => setRedraftNote(e.target.value)}
                    placeholder="e.g. make it shorter, remind them about Friday's deadline"
                    className="bg-jarvis-surface-2 border border-jarvis-border rounded px-3 py-2 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => redraft.mutate(redraftNote)}
                      disabled={!redraftNote.trim() || redraft.isPending}
                      className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-3 py-1.5 rounded-lg text-sm font-medium"
                    >
                      {redraft.isPending ? 'Rewriting…' : 'Rewrite'}
                    </button>
                    <button
                      onClick={() => {
                        setShowRedraft(false)
                        setRedraftNote('')
                      }}
                      className="text-xs uppercase tracking-wider text-jarvis-muted hover:text-jarvis-text"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {showJarvisNoticed ? (
                <JarvisNoticed onDone={onChanged} />
              ) : (
                <div className="flex flex-wrap gap-2 pt-2">
                  {editing ? (
                    <button
                      onClick={() => send.mutate(draftDraft)}
                      disabled={send.isPending}
                      className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      {send.isPending ? 'Sending…' : 'Send Edited Version'}
                    </button>
                  ) : (
                    <button
                      onClick={() => send.mutate(undefined)}
                      disabled={send.isPending}
                      className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      {send.isPending ? 'Sending…' : '✅ Send This'}
                    </button>
                  )}
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="bg-jarvis-surface-2 hover:bg-jarvis-surface border border-jarvis-border px-4 py-2 rounded-lg text-sm"
                    >
                      ✏ Edit First
                    </button>
                  )}
                  <button
                    onClick={() => setShowRedraft((s) => !s)}
                    className="bg-jarvis-surface-2 hover:bg-jarvis-surface border border-jarvis-border px-4 py-2 rounded-lg text-sm"
                  >
                    🔄 Redraft
                  </button>
                  <button
                    onClick={() => skip.mutate()}
                    disabled={skip.isPending}
                    className="text-xs uppercase tracking-wider text-jarvis-muted hover:text-jarvis-red ml-auto self-center disabled:opacity-50"
                  >
                    {skip.isPending ? 'Skipping…' : '✕ Skip'}
                  </button>
                </div>
              )}

              {send.error && (
                <p className="text-xs text-jarvis-red">
                  {(send.error as Error).message}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </JarvisCard>
  )
}

function JarvisNoticed({ onDone }: { onDone: () => void }) {
  return (
    <div className="border border-jarvis-blue/30 bg-jarvis-blue/5 rounded-lg p-4 text-sm">
      <p className="font-medium text-jarvis-text mb-1">🧠 Jarvis noticed</p>
      <p className="text-jarvis-muted mb-3">
        You made significant changes to this draft. Jarvis will look at edits
        like this when it tunes your style profile next Sunday.
      </p>
      <div className="flex gap-2">
        <button
          onClick={onDone}
          className="bg-jarvis-blue hover:bg-jarvis-blue-bright px-3 py-1.5 rounded text-sm font-medium"
        >
          Got it
        </button>
      </div>
    </div>
  )
}
